<script>
  import Modal from '@components/general/modal.svelte'
  import Form from '@components/forms'
  import Message from '@components/general/message.svelte'
  import Button from '@components/buttons/button.svelte'
  import LoadingArea from '@components/general/loading-area.svelte'
  import { RefreshCw } from 'lucide-svelte'
  import { get, post } from '@rails/request.js'
  import { router } from '@inertiajs/svelte'
  import { formatObject } from '@lib/utils'

  let { sync, lastSyncText, reset } = $props()

  let folders = $state({ loading: true, data: [], id: null })

  let formDisabled = $derived(folders.loading || !!sync.results)

  let folderOptions = $derived.by(() => {
    if (folders.loading) return [{ label: 'Loading...', value: null, disabled: true }]
    const options = folders.data.map(folder => ({ label: folder.name, value: folder.id }))
    const blankOption = { label: 'All folders', value: null }
    return [blankOption, ...options]
  })

  async function getFolders() {
    try {
      folders.loading = true
      folders.error = null
      const response = await get('/api/v1/videos/folders')
      const json = await response.json
      folders.data = json
    } catch (e) {
      folders.error = { message: e?.message || 'Error fetching folders', details: e }
    } finally {
      folders.loading = false
    }
  }

  function formatDetails(value) {
    if (value === null || value === undefined) return ''
    if (typeof value === 'string') return value
    if (typeof value === 'object') return JSON.stringify(value, null, 2)
    return String(value)
  }

  async function performSync() {

    if (!folders.id && !window.confirm(`Are you sure you want to sync videos from ALL folders?`)) return
    
    sync.processing = true
    sync.results = null
    sync.error = null
    try {
      const body = { folder_id: folders.id }
      const response = await post('/api/v1/videos/perform_sync', { body })
      const json = await response.json
      if (!response.ok) {
        sync.error = {
          message: 'Error syncing videos',
          details: json?.errors ?? json,
        }
        return
      }

      sync.results = json
      router.reload()
    } catch (e) {
      sync.error = { message: e?.message || 'Error syncing videos', details: e }
    } finally {
      sync.processing = false
    }
  }

  $effect(async () => {
    if (sync.open) await getFolders()
  })

</script>

<Modal bind:open={sync.open} title="Fetch videos from Vimeo" onClose={reset}>

  {#if sync.processing}
    <LoadingArea message="Fetching videos from Vimeo collection..." />
  {:else if sync.error}
    <Message type="error" title={sync.error.message || "Error syncing videos"}>
      {#if sync.error.details}
        <pre class="text-xs text-gray-700 whitespace-pre-wrap wrap-break-word">{formatDetails(sync.error.details)}</pre>
      {/if}
      <Button variant="link" onclick={reset} text="Clear results" />
    </Message>
  {:else if sync.results}
    <Message type="success" title="Videos fetched successfully">
      <p>{formatObject(sync.results)}</p>
      <Button variant="link" onclick={reset} text="Clear results" />
    </Message>
  {:else}
    <Form.Items>
      <Form.Item name="folder_id" label="Folder" bind:value={folders.id} options={folderOptions} disabled={folders.loading} cols={12} />
    </Form.Items>

    <p class="text-smaller text-gray-500 italic mt-4">{lastSyncText}</p>
  {/if}

  {#snippet footer()}
    <Button variant="primary" onclick={performSync} loading={sync.processing} disabled={formDisabled} Icon={RefreshCw} text="Perform sync" />
  {/snippet}
</Modal>